/* istanbul ignore file */

import type { MutableRefObject } from 'react';
import React, { useRef } from 'react';
import type { CurrencyCode } from '@noths/polaris-dev-ts-types';
import { CurrencySymbol } from '@noths/polaris-dev-ts-types';
import * as R from 'ramda';

import { RangeInput } from 'src/components/molecules/RangeInput/RangeInput';
import type { RangeInputValues } from 'src/components/molecules/RangeInput/types';
import { FilterUid } from 'src/components/organisms/Filter/constants/uids';
import type { FilterOptionsProps } from 'src/components/organisms/Filter/containers/FilterOptionsContainer.types';
import type { Filter, FilterOptionValue, SelectedFilterOption } from 'src/types';
import { getRangeInputProperties } from './getRangeInputProperties';
import { getRangeInputValues } from './getRangeInputValues';

type PrevFilterUpdateRef = (SelectedFilterOption | undefined)[];

export const onChange = (
  newValues: RangeInputValues,
  filter: Filter,
  prevFilterUpdateRef: MutableRefObject<PrevFilterUpdateRef>,
  onSelectFilterOption: FilterOptionsProps['onSelectFilterOption'],
) =>
  newValues.forEach((value: FilterOptionValue, index) => {
    const currentFilterOptionValue =
      Array.isArray(prevFilterUpdateRef.current) && prevFilterUpdateRef.current.length >= index
        ? prevFilterUpdateRef.current[index]
        : null;

    const newFilterOptionValue = {
      filterUid: filter.uid,
      filterOptionUid: filter.options[index].uid,
      filterOptionValue: value,
    } as SelectedFilterOption;

    if (!R.equals(currentFilterOptionValue, newFilterOptionValue)) {
      prevFilterUpdateRef.current[index] = newFilterOptionValue;
      onSelectFilterOption(newFilterOptionValue);
    }
  });

export interface RangeInputWrapperProps {
  currencyCode: CurrencyCode;
  filter: Filter;
  isExpanded?: boolean;
  selectedFilterOptions: SelectedFilterOption[];
  onSelectFilterOption: (selectedFilterOption: SelectedFilterOption) => void;
}

export const RangeInputWrapper = ({
  currencyCode,
  filter,
  selectedFilterOptions,
  onSelectFilterOption,
}: RangeInputWrapperProps) => {
  const { step, range, handleLabels, handleInputNames } = getRangeInputProperties(filter);
  const values = getRangeInputValues(selectedFilterOptions, filter.uid, range, filter.options);
  const valueTextPrefix = filter.uid === FilterUid.Price ? CurrencySymbol[currencyCode] : undefined;

  // (Ref used to avoid additional re-renders on top of prop changes caused by Redux)
  const prevFilterUpdateRef = useRef<PrevFilterUpdateRef>([
    {
      filterUid: filter.uid,
      filterOptionUid: filter.options[0].uid,
      filterOptionValue: values[0],
    },
    {
      filterUid: filter.uid,
      filterOptionUid: filter.options[1].uid,
      filterOptionValue: values[1],
    },
  ]);

  return (
    <RangeInput
      values={values}
      range={range}
      step={step}
      accessibleHandleLabels={handleLabels}
      handleInputNames={handleInputNames}
      valueTextPrefix={valueTextPrefix}
      onChange={(newValues) =>
        onChange(newValues, filter, prevFilterUpdateRef, onSelectFilterOption)
      }
    />
  );
};
